<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted, onUpdated } from "vue";

import { STATUS, type StateChanger, DIRECTION, type ScrollEvent } from "./interfaces";

import TcLoader from "../TcLoader.vue";

import { hasValue, isObject } from "@/Utils/Objects";

export interface InfiniteProps {
  parentId: string;
  direction?: DIRECTION;
  scrollDistance?: number;
}

const props = withDefaults(defineProps<InfiniteProps>(), {
  direction: DIRECTION.BOTTOM,
  scrollDistance: 100,
});

const emit = defineEmits<{
  (event: "infinite", value: StateChanger): void;
  (event: "scroll", value: ScrollEvent): void;
}>();

const infiniteDomElement = ref<HTMLElement | null>(null);
const stateChanger = ref<StateChanger>();
const scrollParent = ref<Element>();
const isFirstLoad = ref<boolean>(false);
const status = ref(STATUS.READY);

const isShowSpinner = computed(() => status.value === STATUS.LOADING);
const isShowError = computed(() => status.value === STATUS.ERROR);
const isShowNoResults = computed(() => status.value === STATUS.COMPLETE && isFirstLoad.value);
const isShowNoMore = computed(() => status.value === STATUS.COMPLETE && !isFirstLoad.value);

const addEventListener = (): void => {
  if (scrollParent.value) {
    scrollParent.value.addEventListener("scroll", scrollHandler);
  }
};

const removeEventListener = (): void => {
  if (scrollParent.value) {
    scrollParent.value.removeEventListener("scroll", scrollHandler);
  }
};

const getCurrentDistance = (): number => {
  if (!scrollParent.value) return 0;

  let distance;

  if (props.direction === DIRECTION.TOP) {
    distance = typeof scrollParent.value.scrollTop === "number" ? scrollParent.value.scrollTop : 0;
  } else {
    const currentScroll = scrollParent.value.scrollTop;
    const parentHeight = scrollParent.value.scrollHeight;
    const parentViewHeight = scrollParent.value.clientHeight;

    distance = parentHeight - parentViewHeight - currentScroll;
  }

  return distance;
};

const isVisible = (elm: HTMLElement): boolean => {
  return (
    isObject(elm) &&
    hasValue(elm.offsetWidth) &&
    hasValue(elm.offsetHeight) &&
    elm.offsetWidth + elm.offsetHeight > 0
  );
};

const getScrollParent = (): HTMLElement => {
  const result = document.getElementById(props.parentId);

  if (result === null || result === undefined)
    throw new Error("Invalid parent id defined. A valid parent id is required");

  return result;
};

const infinityLoad = (): void => {
  if (
    ![STATUS.COMPLETE, STATUS.LOADING].includes(status.value) &&
    isVisible(infiniteDomElement.value as HTMLElement) &&
    getCurrentDistance() <= props.scrollDistance
  ) {
    status.value = STATUS.LOADING;

    if (stateChanger.value) {
      emit("infinite", stateChanger.value);
    }
  } else {
    if (status.value === STATUS.LOADING) {
      status.value = STATUS.READY;
    }
  }
};

const emitScrollEvent = () => {
  emit("scroll", {
    distance: getCurrentDistance(),
    viewHeight: !scrollParent.value ? 0 : scrollParent.value.clientHeight,
    totalScroll: !scrollParent.value ? 0 : scrollParent.value.scrollHeight,
  });
};

const scrollHandler = (): void => {
  if (status.value === STATUS.READY) {
    infinityLoad();
  }

  emitScrollEvent();
};

onUnmounted(() => {
  status.value = STATUS.COMPLETE;

  removeEventListener();
});

onMounted(() => {
  scrollParent.value = getScrollParent();

  stateChanger.value = {
    getStatus: () => status.value,

    loaded: () => {
      isFirstLoad.value = false;
      infinityLoad();
    },

    complete: () => {
      status.value = STATUS.COMPLETE;
    },

    reset: () => {
      status.value = STATUS.READY;
      isFirstLoad.value = true;
      scrollParent.value = getScrollParent();

      addEventListener();

      setTimeout(() => {
        infinityLoad();
      }, 500);
    },

    error: () => {
      status.value = STATUS.ERROR;
    },
  };

  setTimeout(() => {
    infinityLoad();
    addEventListener();

    emitScrollEvent();
  }, 500);
});

onUpdated(() => {
  scrollParent.value = getScrollParent();
});
</script>

<template>
  <div ref="infiniteDomElement">
    <div v-if="isShowSpinner">
      <slot name="spinner">
        <TcLoader class="py-4" />
      </slot>
    </div>

    <div v-if="isShowNoResults">
      <slot name="no-results" />
    </div>

    <div v-if="isShowNoMore">
      <slot name="no-more" />
    </div>

    <div v-if="isShowError">
      <slot name="error" />
    </div>
  </div>
</template>
